import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components'
import { Form, Input, Switch, Checkbox, InputNumber, Tag, Tabs, notification, Modal } from 'antd';
import * as _ from 'lodash';
import UserChannels from './UserChannels';
import UserPaths from './UserPaths';
import UserSettings from './UserSettings';
import UserGroups from './UserGroups';
import axios from 'axios';
import { getObjectDiffs } from '../../../utils';
import { Icon } from 'dlweb-components'
import { ExclamationCircleOutlined } from '@ant-design/icons';

const UserForm = forwardRef(({userIds, setDisabled, setLoading, rootScope, location, isAdmin, id, refreshUsers, shouldLoad}, ref) => {

  const { TabPane } = Tabs;

  const [form] = Form.useForm();
    
  const scrollRef = useRef(null)
  const detailsRef = useRef(null);
  const usernamesRef = useRef(null);

  const { confirm } = Modal;

  const [scrolled, setScrolled] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);

  const [dirtyForms, setDirtyForms] = useState(false);
  const [tabItems, setTabItems] = useState([]);
  const [selectedTab, setSelectedTab] = useState("settings")
  const [dirtyTabs, setDirtyTabs] = useState(["settings"]);

  const [initialValues, setInitialValues] = useState({});
  const [initialSettingsValues, setInitialSettingsValues] = useState({});
  const [initialPathsValues, setInitialPathsValues] = useState({});

  const [groups, setGroups] = useState([]);
  const [dirtyGroups, setDirtyGroups] = useState([]);
  const [pathError, setPathError] = useState(false);
  
  const [formItemLayout] = useState({
    labelCol: {
        xs: { span: 8 },
        sm: { span: 8 },
    },
    wrapperCol: {
        xs: { span: 16 },
        sm: { span: 16 },
    },
  });

  useEffect(() => {
    if(dirtyForms) {
      const off = rootScope.$on('$locationChangeStart', (event, newUrl) => {
        if(!_.includes(newUrl, '/logout')) {
          event.preventDefault();
          if(window.confirm("Unsaved changes will be lost when you leave this page. Continue?")) {
            off();
            location.path(newUrl);
          }
        }
      })
      return () => off();
    }
  }, [rootScope, location, dirtyForms])

  useImperativeHandle(ref, () => ({
    onOk: (action) => {
      form.submit();
    }
  }))

  useEffect(() => {
    !_.isEmpty(initialValues) && form?.resetFields();
  }, [form, initialValues])

  useEffect(() => {

    const handleScroll = (e) => {
      setScrolled(e.srcElement.scrollTop !== 0);
    }

    const ref = scrollRef;
    ref.current && ref.current.addEventListener('scroll', handleScroll)

    return () => {
      ref.current && ref.current.removeEventListener('scroll', handleScroll)
    }

  }, [])

  const loadData = useCallback((tabId, url, setFormValues, params) => {
    if(userIds.length > 0) {

      let options = {};

      if(url.indexOf('memberships') > -1 && !params) {
        options = {
          limit: 50,
          skip: 0
        }
      }

      axios.post(url, { ids: userIds, ...options }).then((res) => {
        if(res.data.errors.length > 0) {
          notification.error({ message: `Error getting data.` });
        }
        else {
          url.indexOf('generals') > -1 && setIsEnabled(res.data.data.enabled)

          if(tabId === 'groups') {
            setFormValues(res.data);
          }
          else {
            setFormValues(res.data.data);
          }


          tabId && setDirtyTabs(t => [...t, tabId]);
          setShowLoadingScreen(false);
        }
      })
    }
    else {
      setFormValues({});
    }
  }, [userIds, setIsEnabled])

  const loadTabData = useCallback((activeTab) => {

    if(!shouldLoad) return;

    switch (activeTab) {
      case 'settings':
        loadData(activeTab, 'api/wt/userman/settings/read', setInitialSettingsValues);
        break;
      case 'groups':
        setGroups([]); // need to do this or the ant grid will not update
        loadData(activeTab, 'api/wt/userman/memberships/read', setGroups);
        break;
      case 'paths':
        loadData(activeTab, 'api/wt/userman/paths/multi/read', setInitialPathsValues);
        break;
      default:
        break;
    }    
  }, [shouldLoad, setInitialSettingsValues, setGroups, setInitialPathsValues, loadData])

  const onGroupPageChange = useCallback((pageParams) => {
    loadData('groups', 'api/wt/userman/memberships/read', setGroups, pageParams);
  }, [loadData])

  useEffect(() => {    
    if (_.includes(dirtyTabs, selectedTab)) return;

    loadTabData(selectedTab);
  }, [userIds, selectedTab, dirtyTabs, loadTabData])

  const onTabsChange = (activeKey) => {
    setSelectedTab(activeKey);
  }

  useEffect(() => {

    setShowLoadingScreen(true);
    
    if(userIds.length > 0 && shouldLoad) {
      setDisabled(true);
      loadData(null, 'api/wt/userman/generals/read', setInitialValues);
    }

    return () => {
      setPathError(false);
      setDirtyTabs([]);
      setDirtyGroups([]);
      setInitialSettingsValues({});
      setInitialPathsValues({});
      setSelectedTab('settings');
    }
  }, [form, userIds, setDisabled, loadData, setSelectedTab])

  const onGroupChanged = useCallback((groupData) => {
    
    setDirtyGroups(g => {
      
      let dg = [];

      if(!!_.find(groups.data, groupData)) {
        dg = _.filter(g, o => o.groupId !== groupData.groupId)
      }
      else {
        dg = [...g, groupData]
      }

      return dg;
    })
  }, [groups])

  const handleSubmit = (name, values, forms) => {

    if (name === 'editChannelForm') return;

    setLoading(true);

    const { userForm, userSettingsForm, userPathsForm } = forms;

    const generalValues = userForm?.getFieldsValue(true);
    const settingsValues = userSettingsForm?.getFieldsValue(true);
    const pathValues = userPathsForm?.getFieldsValue(true);
    
    const updateUserData = () => {
  
    let updateGeneral = null;
    let updateSettings = null;
    let updatePaths = null;
    let updateGroups = null;
    
    // save general settings
    if (!_.isEqual(generalValues, initialValues)) {
      const generalData = getObjectDiffs(generalValues, initialValues);
      updateGeneral = axios
        .post("/api/wt/userman/generals/update", {
          ids: userIds,
          generals: generalData,
        })
        .then((res) => {
          setInitialValues(generalValues);
          return res;
        })
        .catch((err) => {
          notification.error({ message: `User update failed.` });
        });
    }

    // save settings tab if it has changes
    if (!_.isEqual(settingsValues, initialSettingsValues)) {
      const settingsData = getObjectDiffs(
        settingsValues,
        initialSettingsValues
      );
      updateSettings = axios
        .post("/api/wt/userman/settings/update", {
          ids: userIds,
          settings: settingsData,
        })
        .then((res) => {
          setInitialSettingsValues(settingsValues);
          return res;
        })
        .catch((err) => {
          notification.error({ message: `User settings update failed.` });
        });
    }

    // save paths tab if it has changes
    if (pathValues && !_.isEqual(pathValues, initialPathsValues)) {

      const pathsData = getObjectDiffs(pathValues, initialPathsValues);
      updatePaths = axios
        .post("/api/wt/userman/paths/multi/update", {
          ids: userIds,
          paths: pathsData,
        })
        .then((res) => {
          setInitialPathsValues(pathValues);
        })
        .catch((err) => {
          notification.error({ message: `User paths update failed.` });
        });
    }

      // save groups tab if it has changes
      if(dirtyGroups.length > 0) {

        updateGroups = axios.post('/api/wt/userman/memberships/update', { ids: userIds, memberships: dirtyGroups }).then((res) => {

          const newGroups = {
            data: groups.data.map(o => {
              const newValue = _.find(dirtyGroups, ['groupId', o.groupId]);
              return newValue ? newValue : o
            }),
            _meta: groups._meta
          };

          setGroups(newGroups);

          setDirtyGroups([]);

          return res;
        }).catch((err) => {
          console.log(err)
          notification.error({ message: `User groups update failed.` })
        });
      }

      axios.all([updateGeneral, updateSettings, updatePaths, updateGroups]).then(axios.spread((resGeneral, resSettings, resPaths, resGroups) => {

        if(disableCurrentUser) {
          location.url('/logout');
          rootScope.$apply();
          return;
        }

        if(resGeneral || resSettings || resPaths || resGroups) {
          notification.success({ message: `User updated successfully.` });
          refreshUsers(false);
        }

        setDisabled(true);
        setLoading(false);
        setDirtyForms(false);
        
      }))
    }

    // check to see if they're disabling their own user
    const disableCurrentUser = id !== 'cssadmin' && !generalValues.enabled && _.includes(userIds, id);
    if(disableCurrentUser) {

      const config = {
        icon: <ExclamationCircleOutlined />,
        title: 'You are disabling your own account!',
        content: (
          <>
            If you save these changes your account will be immediately disabled, and you will be logged out of WebTools.
            <br /><br />
            Continue?
          </>
        ),
        onOk() {
          updateUserData();
        },
        onCancel() {
          setLoading(false);
          return;
        }
      };

      confirm(config)
    }
    else {
      updateUserData();
    }
  };

  const onEnabledChanged = (checked) => {
    setIsEnabled(checked);

    if(_.includes(userIds, 'cssadmin') && !checked) {
      Modal.info({ title: 'Cannot disable cssadmin', content: `User 'cssadmin' is selected, but will not be disabled along with the other selected users.` });
    }
  }

  const checkFormsForChanges = (formName, changedFields, forms) => {

    const { userForm, userSettingsForm, userPathsForm } = forms;

    const pathErrorCount = _.map(_.filter(userPathsForm?.getFieldsError(), (o) => o.errors.length > 0), (item) => item.errors[0]).length;
    setPathError(pathErrorCount !== 0);

    const newSettings = {
      ...initialValues,
      ...userForm.getFieldsValue(),
    }

    const mainFormTouched = _.isEqual(initialValues, newSettings);
  
    const disableButton = mainFormTouched &&
                          !userSettingsForm.isFieldsTouched() &&
                          dirtyGroups.length === 0 &&
                          !userPathsForm?.isFieldsTouched() ||
                          pathErrorCount !== 0

    setDisabled(disableButton);
    setDirtyForms(!disableButton);
  }

  useEffect(() => {
    setDisabled(dirtyGroups.length === 0 || pathError);
    setDirtyForms(dirtyGroups.length !== 0);
  }, [dirtyGroups, pathError, setDisabled, setDirtyForms])



  useEffect(() => {

    const settingsContent = (
      <TabContent>
        <UserSettings
          userIds={userIds}
          initialValues={initialSettingsValues}
          formItemLayout={formItemLayout}
        />
      </TabContent>
    );

    const groupContent = (
      <TabContent>
        <UserGroups
          userIds={userIds}
          groupData={groups}
          userForm={detailsRef}
          onPageChange={onGroupPageChange}
          onGroupChanged={onGroupChanged}
        />
      </TabContent>
    );

    const pathsContent = (
      <TabContent>
        <UserPaths userIds={userIds} initialValues={initialPathsValues} />
      </TabContent>
    );

    const channelsContent = (
      <TabContent>
        <UserChannels userIds={userIds} selected={selectedTab === "channels"} />
      </TabContent>
    );

    const items = [
      { label: "Settings", key: "settings", children: settingsContent },
      { label: "Paths", key: "paths", children: pathsContent },
      { label: "Channels", key: "channels", children: channelsContent },
    ];

    if (isAdmin) {
      items.splice(1, 0, {
        label: "Groups",
        key: "groups",
        children: groupContent,
      });
    }

    setTabItems(items);

  }, [
    userIds,
    initialSettingsValues,
    formItemLayout,
    isAdmin,
    groups,
    onGroupPageChange,
    onGroupChanged,
    initialPathsValues,
    selectedTab,
  ]);


  return (
    <Form.Provider
      onFormChange={(name, { changedFields, forms }) =>
        checkFormsForChanges(name, changedFields, forms)
      }
      onFormFinish={(name, { values, forms }) =>
        handleSubmit(name, values, forms)
      }
    >
      <UserContainer>
        {
          <>
            <div className="modal-mount"></div>
            <LoadingScreen visible={showLoadingScreen}>
              <Icon
                type="spinner"
                className="fa-spin"
                style={{ fontSize: "60px", margin: "auto", display: "block" }}
              />
            </LoadingScreen>

            <UserNamesContainer ref={usernamesRef} scrolled={scrolled}>
              <div
                style={{
                  marginLeft: "0px",
                  fontSize: "14px",
                  color: "#000000d9",
                  position: "absolute",
                  left: "25px",
                  top: "22px",
                }}
              >
                Login:
              </div>
              <UserNames>{userIds.join(", ")}</UserNames>
            </UserNamesContainer>

            <ScrollingContainer ref={scrollRef} usernamesRef={usernamesRef}>
              <Form
                form={form}
                name="userForm"
                {...formItemLayout}
                labelAlign="left"
                initialValues={initialValues}
                autoComplete="off"
                hideRequiredMark
              >
                <DetailsHeader ref={detailsRef}>
                  {/* <Form.Item label="Login" style={{ marginBottom: '8px' }}>
                  <UserName>
                    {userIds.join(', ')}
                  </UserName>
                </Form.Item> */}
                  {/*
                  this nesting method from the antd documentation currently throws an error when navigating away from the page
                  will revisit and test again when we upgrade antd

                  <Form.Item label="Status" style={{ marginBottom: '4px' }}>
                    <Form.Item name="enabled" valuePropName="checked" noStyle>
                      <Switch style={{marginTop: '-2px'}} />
                    </Form.Item>
                    <Tag color={settings.enabled ? 'green' : 'red'} style={{marginLeft: '10px'}}>
                      {settings.enabled ? 'Enabled' : 'Disabled'}
                    </Tag>
                  </Form.Item>
                */}
                  <SwitchContainer>
                    <Form.Item
                      label="Status"
                      name="enabled"
                      valuePropName="checked"
                      style={{ marginBottom: "4px" }}
                    >
                      <Switch
                        style={{ marginTop: "-2px" }}
                        onChange={(checked) => onEnabledChanged(checked)}
                        disabled={
                          userIds.length === 1 && userIds[0] === "cssadmin" || !isAdmin
                        }
                      />
                    </Form.Item>
                    <TagContainer>
                      <Tag color={isEnabled ? "green" : "red"}>
                        {isEnabled ? "Enabled" : "Disabled"}
                      </Tag>
                    </TagContainer>
                  </SwitchContainer>
                  <Form.Item
                    label=" "
                    colon={false}
                    name="namedUser"
                    valuePropName="checked"
                    style={{
                      marginTop: "-5px",
                      marginBottom:
                        initialValues.authenticationType !== 1 ? "4px" : 0,
                    }}
                  >
                    <Checkbox>Use Named User License</Checkbox>
                  </Form.Item>
                  {initialValues.authenticationType === 1 && 
                  ((userIds.length === 1 && userIds[0] !== "cssadmin") || userIds.length > 1) &&
                  (
                    <div style={{ height: "72px", marginTop: "-5px" }}>
                      <Form.Item
                        label=" "
                        colon={false}
                        name="passwordChangeNextLogin"
                        valuePropName="checked"
                        style={{ marginBottom: "0px" }}
                      >
                        <Checkbox disabled={!isAdmin}>Require new password next login</Checkbox>
                      </Form.Item>

                      <SwitchContainer>
                        <Form.Item
                          label=" "
                          colon={false}
                          name="passwordChangeEveryDays"
                        >
                          <InputNumber
                            min={0}
                            step={1}
                            max={999}
                            style={{ width: "60px" }}
                            disabled={!isAdmin}
                          />
                        </Form.Item>
                        <TagContainer
                          style={{
                            right: "15px",
                            marginBottom: "0",
                          }}
                        >
                          Password expiration interval (days)
                        </TagContainer>
                      </SwitchContainer>
                    </div>
                  )}
                </DetailsHeader>
                <GeneralSettings>
                  <Form.Item
                    label="First Name"
                    name="firstName"
                    style={{ marginBottom: "8px" }}
                  >
                    <Input style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item
                    label="Last Name"
                    name="lastName"
                    style={{ marginBottom: "8px" }}
                  >
                    <Input style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item
                    label="Job Title"
                    name="title"
                    style={{ marginBottom: "8px" }}
                  >
                    <Input style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item
                    label="Department"
                    name="department"
                    style={{ marginBottom: "8px" }}
                  >
                    <Input style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item
                    label="Company"
                    name="company"
                    style={{ marginBottom: "8px" }}
                  >
                    <Input style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item
                    label="Manager ID"
                    name="supervisor"
                    style={{ marginBottom: "8px" }}
                  >
                    <Input style={{ width: "100%" }} />
                  </Form.Item>
                </GeneralSettings>
              </Form>
              <Tabs
                type="cards"
                activeKey={selectedTab}
                defaultActiveKey="1"
                onChange={onTabsChange}
                tabBarStyle={{ width: "100%" }}
                destroyInactiveTabPane={false}
                items={tabItems}
              />
            </ScrollingContainer>
          </>
        }
      </UserContainer>
    </Form.Provider>
  );
})

const UserContainer = styled.div`
  height: 100%;
  //display: 'flex';
  //flex-direction: 'column';
  background-color: #fff;
  .ant-tabs-nav,
  .ant-tabs-nav-wrap,
  .ant-tabs-nav-list {
    width: 480px;
    padding: 0;
    margin: 0;
    //display: inline-block !important;
  }
  .ant-tabs-tab,
  .ant-tabs-tab-active  {
    width: 25%;
    margin: 0;
    border-bottom: none !important;
    text-align: center;
    display: inline-block !important;
  }
  .modal-mount .ant-modal-mask,
  .modal-mount .ant-modal-wrap {
    position: absolute;
  }
  .ant-tabs-ink-bar {
    position: absolute;
    background: inherit;
    pointer-events: none;
  }
  .ant-tabs-content-holder {
    border-bottom: none;
  }
`

const DetailsHeader = styled.div`
  width: 500px;
  color: #333;
  padding: 0 25px;
  overflow: hidden;
  flex: 0 0 auto;
  z-index: 2;
`

const SwitchContainer = styled.div`
  position: relative;
`

const LoadingScreen = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 51px;
  left: 0;
  background: #fff;
  z-index: 10;
  opacity: .6;
  display: ${p => p.visible ? 'block' : 'none'};
  padding-top: 200px;
  color: #2269a4;
`

const ScrollingContainer = styled.div`
  //height: 100vh;
  width: 100%;
  flex: 1 1 auto;
  margin-bottom: 130px;
  overflow-y: auto;
  overflow-x: hidden;
  height: ${p => `calc(100vh - 140px - ${p.usernamesRef.current?.offsetHeight}px)`};
`

const UserNamesContainer = styled.div`
  padding: 20px 25px 15px 25px;
  position: relative;
  z-index: 999;
  box-shadow: ${p => p.scrolled ? '0px 4px 10px -8px rgba(0,0,0,0.48)' : 'none'};
`

const UserNames = styled.div`
  width: 300px;
  font-size: 16px;
  font-weight: 500;
  color: #000;
  max-height: 60px;
  overflow-y: auto;
  margin-left: 152px;
  display: inline-block;
`

const TagContainer = styled.div`
  position: absolute;
  top: 5px;
  right: 175px;
`

const GeneralSettings = styled.div`
  width: 500px;
  padding: 0 25px;
  margin-bottom: 25px;
  box-sizing: border-box;
`

const TabContent = styled.div`
  width: 500px;
  padding: 25px;
`

const mapStateToProps = state => {
  const { id } = state.user;
  return { id };
};

export default connect(mapStateToProps, null, null, {forwardRef: true})(UserForm);
